import React from 'react';

interface ServiceCostItem {
  service: string;
  cost: number;
  percentage: number;
}

interface ServiceBreakdownChartProps {
  data: ServiceCostItem[] | null;
  loading: boolean;
}

const ServiceBreakdownChart: React.FC<ServiceBreakdownChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Cost by AWS Service
        </h3>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Cost by AWS Service
        </h3>
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          No cost data available
        </div>
      </div>
    );
  }

  // Colors for the services - blue shades only
  const colors = [
    '#3B82F6', // blue-500
    '#60A5FA', // blue-400
    '#2563EB', // blue-600
    '#93C5FD', // blue-300
    '#1D4ED8', // blue-700
    '#BFDBFE', // blue-200
    '#1E40AF', // blue-800
  ];

  // Take top 5 services and group the rest as "Other"
  const topServices = data.slice(0, 5);
  const otherServices = data.slice(5);
  const otherCost = otherServices.reduce((sum, s) => sum + s.cost, 0);
  const otherPercentage = otherServices.reduce((sum, s) => sum + s.percentage, 0);

  const chartData = [...topServices];
  if (otherCost > 0) {
    chartData.push({
      service: 'Other',
      cost: otherCost,
      percentage: otherPercentage,
    });
  }

  // Calculate pie chart segments
  const createPieSegments = () => {
    let cumulativePercentage = 0;
    return chartData.map((service, index) => {
      const percentage = service.percentage;
      const startAngle = (cumulativePercentage / 100) * 360;
      const endAngle = ((cumulativePercentage + percentage) / 100) * 360;
      cumulativePercentage += percentage;

      // Convert angles to radians
      const startRad = (startAngle - 90) * (Math.PI / 180);
      const endRad = (endAngle - 90) * (Math.PI / 180);

      // Calculate SVG path
      const radius = 80;
      const x1 = 100 + radius * Math.cos(startRad);
      const y1 = 100 + radius * Math.sin(startRad);
      const x2 = 100 + radius * Math.cos(endRad);
      const y2 = 100 + radius * Math.sin(endRad);

      const largeArcFlag = percentage > 50 ? 1 : 0;

      const pathData = [
        `M 100 100`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        `Z`,
      ].join(' ');

      return {
        pathData,
        color: colors[index % colors.length],
        service: service.service,
        percentage: service.percentage,
        cost: service.cost,
      };
    });
  };

  const segments = createPieSegments();

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Cost by AWS Service
      </h3>

      <div className="flex flex-col items-center">
        {/* Pie Chart */}
        <svg width="200" height="200" viewBox="0 0 200 200" className="mb-6">
          {segments.map((segment) => (
            <path
              key={segment.color}
              d={segment.pathData}
              fill={segment.color}
              stroke="white"
              strokeWidth="2"
              className="transition-opacity hover:opacity-80 cursor-pointer"
            />
          ))}
        </svg>

        {/* Legend */}
        <div className="w-full space-y-2">
          {chartData.map((service, index) => (
            <div key={service.service} className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className="w-3 h-3 rounded-sm mr-2"
                  style={{ backgroundColor: colors[index % colors.length] }}
                ></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {service.service}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {service.percentage.toFixed(1)}%
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ${service.cost.toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServiceBreakdownChart;
